import { Node } from "@tiptap/core";
import { isHttpUrl } from "../../../../../Helper/sanitize";

export interface IframeOptions {
  allowFullscreen: boolean;
  HTMLAttributes: {
    [key: string]: any;
  };
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    iframe: {
      setIframe: (options: { src: string }) => ReturnType;
    };
  }
}

export default Node.create<IframeOptions>({
  name: "iframe",
  group: "block",
  selectable: true,

  addOptions() {
    return {
      allowFullscreen: true,
      HTMLAttributes: {
        class: "iframe-wrapper",
      },
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        // Only an absolute http(s) URL is ever rendered as the source: a
        // `javascript:` or relative source would run on the widget's origin.
        renderHTML: (attributes) =>
          isHttpUrl(attributes.src) ? { src: attributes.src.trim() } : {},
      },
      frameborder: {
        default: 0,
      },
      allowfullscreen: {
        default: this.options.allowFullscreen,
        parseHTML: () => this.options.allowFullscreen,
      },
      sandbox: {
        default:
          "allow-scripts allow-forms allow-top-navigation allow-presentation allow-same-origin allow-popups",
      },
      ratio: {
        default: "landscape",
        parseHTML: (element) =>
          element.getAttribute("data-ratio") === "portrait"
            ? "portrait"
            : "landscape",
        renderHTML: (attributes) => ({ "data-ratio": attributes.ratio }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "iframe",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    let wrapperClass = this.options.HTMLAttributes.class;

    if (node.attrs.ratio === "portrait") {
      wrapperClass += " iframe-wrapper--portrait";
    }

    return [
      "div",
      {
        ...this.options.HTMLAttributes,
        class: wrapperClass,
        "data-type": "iframe",
      },
      [
        "iframe",
        {
          // Defer off-screen embeds so an article with many video players
          // doesn't boot all of them at once (#147262).
          loading: "lazy",
          ...HTMLAttributes,
          onclick:
            "event.stopPropagation(); this.closest('[data-type=\"iframe\"]').classList.toggle('has-focus')",
        },
      ],
    ];
  },

  renderText() {
    return "";
  },

  addCommands() {
    return {
      setIframe:
        (options: { src: string }) =>
        ({ tr, dispatch }) => {
          const { selection } = tr;
          const node = this.type.create(options);

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node);
          }

          return true;
        },
    };
  },
});
